import { html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import type { EmployeeForStaffingCalendar } from 'src/store/selectors';
import { isInvalidPeriod } from 'src/store/selectors';
import { BaseDialog } from 'src/library/components/BaseDialog.js';
import { t } from 'src/i18n';
import type { DEditPeriods } from 'src/pages/d-edit-periods';
import './d-edit-periods';
import type { EditPeriodsInput } from './edit-periods-input';
import type { EditPeriodsResult } from './edit-periods-result';
import type { PeriodEditItem } from './period-edit-item';
import type { DialogActionInput } from 'src/library/components/DialogActionInput';

/**
 *
 * FIX must sync with master. Calculations have changed
 * Hente ny funksjonalitet, vise info og alerts for fraværstyper
 *
 * USAGE:
 *    d-timekeeping-list-section
 *    d-staffing-calendar-table
 *    d-staffing-employee-day-popup
 *
 */
@customElement('edit-periods-dialog')
export class EditPeriodsDialog extends BaseDialog<EditPeriodsInput, EditPeriodsResult> {
  static readonly styles = [...BaseDialog.styles];
  @property({ type: String })
  application: 'timekeeping' | '' = '';
  @property({ type: Object })
  editItem: PeriodEditItem = {
    type: 'vacation',
    confirmed: false,
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    notes: '',
    periodId: '',
    grade: 100,
    days: [],
  };
  @property({ type: Boolean })
  newPeriod = false;
  @property({ type: Object })
  employee!: EmployeeForStaffingCalendar;
  @property({ type: Number })
  width = 700;
  @property({ type: Boolean })
  currentUserHasAccess = false;
  @property({ type: Boolean })
  leavePeriodEditRestriction = false;
  @query('d-edit-periods')
  editPeriodsElm!: DEditPeriods;

  get periodIsInvalid() {
    return isInvalidPeriod(
      this.editItem.startDate,
      this.editItem.startTime,
      this.editItem.endDate,
      this.editItem.endTime,
    );
  }

  protected get calculatedTitle() {
    let employeeName = '';
    if (this.employee) {
      employeeName = this.employee.name;
    }

    if (this.application === 'timekeeping') {
      let typeTerm = t('periods.typeTermTimeOff');
      if (this.editItem.type === 'plusTime') {
        typeTerm = t('periods.typeTermPlusTime');
      }
      if (this.newPeriod) {
        return t('periods.newPeriodForType', { typeTerm, name: employeeName });
      }
      return t('periods.editPeriodForType', { typeTerm, name: employeeName });
    }
    if (this.newPeriod) {
      return t('periods.newLeavePeriodFor', { name: employeeName });
    }
    return t('periods.editLeavePeriodFor', { name: employeeName });
  }

  protected get calculatedFooterActions(): DialogActionInput[] {
    return this.newPeriod
      ? [
          { name: t('common:cancel'), action: 'close' },
          { name: t('periods.done'), action: 'save', main: true, disabled: this.periodIsInvalid },
        ]
      : [
          { name: t('common:delete'), action: 'delete' },
          { name: t('common:cancel'), action: 'close' },
          { name: t('periods.done'), action: 'save', main: true },
        ];
  }

  protected fetchResult(detail: string | undefined): EditPeriodsResult {
    if (detail === 'save') {
      return {
        action: 'done',
        end: this.editItem.endDate + (this.editItem.endTime === 'NONE' ? '' : ' ' + this.editItem.endTime),
        start: this.editItem.startDate + (this.editItem.startTime === 'NONE' ? '' : ' ' + this.editItem.startTime),
        type: this.editItem.type,
        confirmed: this.editItem.confirmed,
        notes: this.editItem.notes,
        grade: this.editItem.grade,
        days: this.editPeriodsElm.calculatedDays,
      };
    }

    if (detail === 'delete') {
      return {
        action: 'delete',
      };
    }

    return {
      action: 'cancel',
    };
  }

  protected initializeDialog(input: EditPeriodsInput) {
    this.editItem = {
      type: input.type,
      confirmed: input.confirmed,
      startDate: input.startDate,
      startTime: input.startTime === '' ? 'NONE' : input.startTime,
      endDate: input.endDate,
      endTime: input.endTime === '' ? 'NONE' : input.endTime,
      notes: input.notes,
      periodId: '',
      grade: input.grade,
      days: input.days,
    };
    this.employee = input.employee;
    this.application = input.application;
    this.newPeriod = input.newPeriod;
    this.currentUserHasAccess = input.currentUserHasAccess;
    this.leavePeriodEditRestriction = input.leavePeriodEditRestriction;
  }

  protected renderBody() {
    return html`
      <d-edit-periods
        .application=${this.application}
        .employee=${this.employee}
        .newPeriod=${this.newPeriod}
        .currentUserHasAccess=${this.currentUserHasAccess}
        .leavePeriodEditRestriction=${this.leavePeriodEditRestriction}
        .editItem=${this.editItem}
        .days=${this.editItem.days}
        @edit-item-changed=${(e: CustomEvent<PeriodEditItem>) => (this.editItem = e.detail)}
      ></d-edit-periods>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'edit-periods-dialog': EditPeriodsDialog;
  }
}
